import { Typography } from "antd";
import ActionMenuV2 from "components/ActionMenuV2";
import message from "components/CustomMessage";
import TableContainer from "components/TableContainer";
import { PermissionSetMap } from "constants/RBACConstants";
import _ from "lodash";
import React from "react";
import { useCallback, useMemo } from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { CellRenderers, columnRenderArrayData } from "utils/tableUtils";

import { JiraCMDBDataProps, NameIdProps } from "./JiraCMDBMappingForm";

type JiraCMDBConfigListProps = {
  onEditConfig?: (data: any) => void;
  onUpdateConfigComponent?: () => any;
  loginConfigId: number;
  data: JiraCMDBDataProps[];
  fetchData: () => void;
};

type TableContainerProps = {
  dataSource?: JiraCMDBDataProps[];
  columns?: any[];
  enableScroll?: boolean;
};

type ActionMenuProps = {
  items?: any[];
  onClick?: (event: any, record: any) => void;
  data?: any[];
};

function JiraCMDBConfigListing({
  data,
  loginConfigId,
  onEditConfig,
  onUpdateConfigComponent,
  ...props
}: JiraCMDBConfigListProps) {
  const onDelete = useCallback(
    async (record) => {
      try {
        await ToolsIntegrationsAPI.deleteJiraCmdbMapping({
          id: record.id,
          toolName: "JIRA",
        }).promise;
        onUpdateConfigComponent && onUpdateConfigComponent();
        message.success("CMDB Configuration deleted successfully.");
      } catch (err) {
        console.log(err);
      }
    },
    [onUpdateConfigComponent],
  );

  const onClick = useCallback(
    async (_: any, record: any) => {
      switch (record.name) {
        case "edit":
          onEditConfig && onEditConfig(record.data);
          break;
        case "delete":
          await onDelete(record.data);
          break;
        default:
          break;
      }
    },
    [onEditConfig, onDelete],
  );

  const items = useMemo(
    () => [
      {
        type: "edit",
      },
      {
        type: "delete",
        permission: PermissionSetMap.DELETE_TOOL,
      },
    ],
    [],
  );

  const columns = useMemo(() => {
    return [
      {
        title: "Schema",
        dataIndex: "schema",
        width: 300,
        render: (schema: NameIdProps) => {
          return (
            <Typography.Text
              className="text-ellipsis"
              style={{ maxWidth: "300px", display: "inline-block" }}
              title={schema.name}
            >
              {CellRenderers.VALUE_OR_NA(schema.name)}
            </Typography.Text>
          );
        },
      },
      {
        title: "Schema Type",
        dataIndex: "schemaType",
        width: 300,
        render: (schemaType: NameIdProps) => {
          return (
            <Typography.Text
              className="text-ellipsis"
              style={{ maxWidth: "300px", display: "inline-block" }}
              title={schemaType.name}
            >
              {CellRenderers.VALUE_OR_NA(schemaType.name)}
            </Typography.Text>
          );
        },
      },
      {
        title: "Attributes",
        dataIndex: "attributes",
        width: 300,
        render: (attributes: string[]) => {
          return columnRenderArrayData(attributes.join());
        },
      },
      {
        title: "Type",
        width: 100,
        dataIndex: "discoveryType",
        render: _.capitalize,
      },
      {
        title: "Action",
        key: "action",
        dataIndex: "action",
        width: 50,
        render: (_: any, record: any) => {
          const ActionMenu =
            ActionMenuV2 as React.ComponentType<ActionMenuProps>;
          return <ActionMenu items={items} onClick={onClick} data={record} />;
        },
      },
    ];
  }, [items, onClick]);

  const TableComponent =
    TableContainer as React.ComponentType<TableContainerProps>;
  return (
    <TableComponent dataSource={data} columns={columns} enableScroll={true} />
  );
}

export default JiraCMDBConfigListing;
